import { Injectable } from '@angular/core';
import { CustomProductSearchPage, CustomSearchCriteria, PageNumbers } from '@catalog/const';
import { MemoizedSelector, Store } from '@ngrx/store';
import { CustomProduct } from '@product/const';
import {
  Breadcrumb,
  PaginationModel,
  ProductActions,
  ProductSearchPage,
  ProductSelectors,
  SearchConfig,
  StateWithProduct,
} from '@spartacus/core';
import { combineLatest, forkJoin, Observable, of, switchMap } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CatalogActions } from '../store/actions';
import { StateWithCatalog } from '../store/catalog-state';
import { CatalogSelectors } from '../store/selectors';

@Injectable({
  providedIn: 'root',
})
export class ProductListFacade {
  constructor(
    private catalogStore: Store<StateWithCatalog>,
    private productStore: Store<StateWithProduct>,
  ) {}

  getLoading(): Observable<boolean> {
    return this.catalogStore.select(CatalogSelectors.getProductListLoading);
  }

  // состояние загрузки во время "Показать еще"
  getMoreLoading(): Observable<boolean> {
    return this.catalogStore.select(CatalogSelectors.getProductListMoreLoading);
  }

  getPageNumbers(): Observable<PageNumbers> {
    return this.catalogStore.select(CatalogSelectors.getProductListLoadingPageNumbers);
  }

  resetPageNumbers(): void {
    this.catalogStore.dispatch(new CatalogActions.LoadMoreProductsReset());
  }

  getLoadingSuggestions(): Observable<boolean> {
    return this.catalogStore.select(CatalogSelectors.getProductListLoadingSuggestions);
  }

  getProducts(): Observable<CustomProduct[]> {
    return this.productStore
      .select(ProductSelectors.getSearchResults as MemoizedSelector<StateWithProduct, CustomProductSearchPage>)
      .pipe(map((searchResults: CustomProductSearchPage) => searchResults.products || []));
  }

  getPagination(): Observable<PaginationModel | undefined> {
    return this.productStore
      .select(ProductSelectors.getSearchResults)
      .pipe(map((searchResults: ProductSearchPage) => searchResults.pagination));
  }

  getBreadcrumbs(): Observable<Breadcrumb[]> {
    return this.productStore
      .select(ProductSelectors.getSearchResults)
      .pipe(map((searchResults: ProductSearchPage) => searchResults.breadcrumbs || []));
  }

  getSearchText(): Observable<string> {
    return this.productStore
      .select(ProductSelectors.getSearchResults)
      .pipe(map((searchResults: ProductSearchPage) => searchResults.freeTextSearch || ''));
  }

  getSnapshotProductsByCodes(productCodes: string[], scope: string): Observable<CustomProduct[]> {
    if (productCodes.length === 0) {
      return of([]);
    }

    const productObservables = productCodes.map((code: string) => this.selectProduct(code, scope).pipe(take(1)));

    return forkJoin(productObservables).pipe(
      map((products: CustomProduct[]) => products.filter((product) => Boolean(product) && product.code)),
    );
  }

  getProductsByCodes(productCodes: string[], scope: string): Observable<CustomProduct[]> {
    if (productCodes.length === 0) {
      return of([]);
    }

    this.loadProductsByCodes(productCodes, scope);

    const loadingObservables = productCodes.map((code) =>
      this.productStore.select(ProductSelectors.getSelectedProductLoadingFactory(code, scope)),
    );

    return combineLatest(loadingObservables).pipe(
      filter((loadingStates) => loadingStates.every((loading) => loading === false)),
      switchMap(() => {
        const productObservables = productCodes.map((code) => this.selectProduct(code, scope).pipe(take(1)));

        return forkJoin(productObservables);
      }),
      map((products) => products.filter((p): p is CustomProduct => Boolean(p))),
    );
  }

  loadProductsByCodes(productCodes: string[], scope: string): void {
    return this.catalogStore.dispatch(new CatalogActions.LoadProductsByCodes(productCodes, scope));
  }

  loadMore(criteria: CustomSearchCriteria): void {
    this.catalogStore.dispatch(new CatalogActions.LoadMoreProducts(criteria));
  }

  search(queryText: string, searchConfig: SearchConfig = {}): void {
    this.productStore.dispatch(
      new ProductActions.SearchProducts({
        queryText,
        searchConfig,
      }),
    );
  }

  getResults(): Observable<ProductSearchPage> {
    return this.productStore.select(ProductSelectors.getSearchResults);
  }

  clearResults(): void {
    this.productStore.dispatch(
      new ProductActions.ClearProductSearchResult({
        clearPageResults: true,
      }),
    );
  }

  private selectProduct(productCode: string, scope: string): Observable<CustomProduct> {
    return combineLatest([
      this.productStore.select(ProductSelectors.getSelectedProductFactory(productCode, scope)),
      this.productStore.select(ProductSelectors.getSelectedProductErrorFactory(productCode, scope)),
    ]).pipe(
      filter(([product, error]: [CustomProduct, boolean]) => Boolean(product || error)),
      map(([product]: [CustomProduct, boolean]) => {
        return product;
      }),
    );
  }
}
